import type { KnowledgeBase } from "@repo/app-types/entities/knowledgeBase";
import { maybeGetKnowledgeBaseExternalProvider } from "@repo/app-types/entities/knowledgeBaseSync";
import type { AdminApiMinimalPlaybook } from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { type FC, useMemo, useState } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { useNavigate } from "react-router";
import {
  useAllKnowledgeBasesQuery,
  useDeleteKnowledgeBaseMutation,
} from "../../../api/entities/knowledgeBase.js";
import { useAllMinimalPlaybooksQuery } from "../../../api/entities/playbook.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { KnowledgeBaseCreateForm } from "./knowledgeBaseCreateForm.js";

export const KnowledgeBasesSettingsSection: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: knowledgeBases, isLoading } = useAllKnowledgeBasesQuery();
  const { data: playbooks, isLoading: isLoadingPlaybooks } =
    useAllMinimalPlaybooksQuery();
  const [deleteKnowledgeBase] = useDeleteKnowledgeBaseMutation();

  // Knowledge base being deleted
  const [deletingKnowledgeBaseId, setDeletingKnowledgeBaseId] = useState<
    string | undefined
  >(undefined);

  // KB id -> playbooks map
  const knowledgeBaseToPlaybooksMap = useMemo(() => {
    const map = new Map<string, AdminApiMinimalPlaybook[]>();

    if (!playbooks) {
      return map;
    }

    playbooks.forEach((playbook) => {
      const knowledgeBaseIds = playbook.settings?.knowledge_base?.ids ?? [];
      knowledgeBaseIds.forEach((kbId) => {
        const existing = map.get(kbId) ?? [];
        map.set(kbId, [...existing, playbook]);
      });
    });
    return map;
  }, [playbooks]);

  const getAssociatedPlaybooks = (knowledgeBaseId: string) => {
    return knowledgeBaseToPlaybooksMap.get(knowledgeBaseId) ?? [];
  };

  const renderRows = () => {
    if (isLoading || isLoadingPlaybooks) {
      return <TableSkeleton />;
    }

    if (!knowledgeBases || knowledgeBases.length === 0) {
      return (
        <SettingsTableEmptyBodyContent colSpan={3}>
          No knowledge bases created yet.
        </SettingsTableEmptyBodyContent>
      );
    }

    return knowledgeBases.map((kb: KnowledgeBase) => {
      const associatedPlaybooks = getAssociatedPlaybooks(kb.id);
      // Sync-owned KBs are read-only: no delete affordance (the API rejects it
      // anyway).
      const isSynced = Boolean(
        maybeGetKnowledgeBaseExternalProvider(kb.metadata),
      );

      return (
        <tr
          key={kb.id}
          className="group relative cursor-pointer hover:bg-gray-50"
          onClick={() =>
            navigate(
              appPaths.settingsKnowledgeBase.build({ knowledgeBaseId: kb.id }),
            )
          }
        >
          <SettingsTableDataCell>
            {kb.name}
            {isSynced ? (
              <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                Synced
              </span>
            ) : null}
          </SettingsTableDataCell>
          <SettingsTableDataCell>
            <p className="text-sm text-muted-foreground">
              {kb.attachment_ids.length}
            </p>
          </SettingsTableDataCell>
          <SettingsTableDataCell>
            {associatedPlaybooks.length > 0 ? (
              <div className="flex flex-col gap-1">
                {associatedPlaybooks.map((p) => (
                  <p key={p.id} className="text-sm text-muted-foreground">
                    - {p.name}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">None</p>
            )}

            {!isSynced ? (
              <div
                className="absolute top-1/2 right-3 hidden -translate-y-1/2 group-hover:flex"
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  variant={ButtonVariant.Secondary}
                  icon={<IconTrash className="size-4" />}
                  onClick={() => setDeletingKnowledgeBaseId(kb.id)}
                />
              </div>
            ) : null}
          </SettingsTableDataCell>
        </tr>
      );
    });
  };

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title={settingsSections.knowledgeBases.label}
        actions={
          <DialogTrigger>
            <Button
              size={Size.SMALL}
              variant={ButtonVariant.Primary}
              icon={<IconPlus className="size-4" />}
            >
              Create knowledge base
            </Button>

            <CenteredModal isDismissable size={Size.LARGE}>
              <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
                {({ close }) => (
                  <>
                    <ModalHeader onClose={close}>
                      Create knowledge base
                    </ModalHeader>
                    <KnowledgeBaseCreateForm close={close} />
                  </>
                )}
              </Dialog>
            </CenteredModal>
          </DialogTrigger>
        }
      />

      <SimpleModal
        isOpen={deletingKnowledgeBaseId !== undefined}
        onCancel={() => setDeletingKnowledgeBaseId(undefined)}
        variant={SimpleModalVariant.Warning}
        title="Delete knowledge base"
        subtitle="Are you sure you want to delete this knowledge base?"
        confirmButtonText="Delete"
        onConfirm={async () => {
          if (deletingKnowledgeBaseId === undefined) {
            return;
          }

          const knowledgeBaseToDelete = knowledgeBases?.find(
            (kb) => kb.id === deletingKnowledgeBaseId,
          );

          if (!knowledgeBaseToDelete) {
            return;
          }

          try {
            await deleteKnowledgeBase(deletingKnowledgeBaseId).unwrap();

            showNotification(dispatch, {
              id: `delete-success-${deletingKnowledgeBaseId}-${Date.now()}`,
              primaryMessage: `Successfully deleted "${knowledgeBaseToDelete.name}"`,
              type: NotificationType.SUCCESS,
            });
          } catch (err: any) {
            showNotification(dispatch, {
              id: `delete-error-${deletingKnowledgeBaseId}-${Date.now()}`,
              primaryMessage: `Failed to delete "${knowledgeBaseToDelete.name}": ${err.data?.error || err.message || "Unknown error"}`,
              type: NotificationType.FAILURE,
            });
          } finally {
            setDeletingKnowledgeBaseId(undefined);
          }
        }}
      />

      <PageBody width={PageWidth.Standard}>
        <SettingsTable>
          <SettingsTableHeaderSection>
            <tr>
              <SettingsTableHeaderCell className="w-[40%]">
                Name
              </SettingsTableHeaderCell>
              <SettingsTableHeaderCell className="w-[20%]">
                # of documents
              </SettingsTableHeaderCell>
              <SettingsTableHeaderCell className="w-[40%]">
                Playbooks
              </SettingsTableHeaderCell>
            </tr>
          </SettingsTableHeaderSection>
          <SettingsTableBodySection>{renderRows()}</SettingsTableBodySection>
        </SettingsTable>
      </PageBody>
    </PageLayout>
  );
};
